import { Box, Grid, Typography, useMediaQuery } from '@mui/material';
import { useEffect } from 'react';
import { styles } from '~/modules/identification/forms/AdditionalInformation/styles.ts';
import { FormInput } from '~/components/FormComponents/FormInput.tsx';
import FormAutocomplete from '~/components/FormComponents/FormAutocomplete.tsx';
import {
  ACCOUNT,
  infoAccountTableColumns,
  JURISDICTION,
  typeAppeals,
} from '~/modules/identification/forms/AdditionalInformation/constants.ts';
import { JurisdictionResidence } from '~/modules/identification/forms/AdditionalInformation/TaxResident/JurisdictionResidence.tsx';
import {
  TAdditionalInfoFormContentProps,
  TPepEntity,
} from '~/modules/identification/forms/AdditionalInformation/types.ts';
import { useGetDefaultPepInfo } from '~/modules/identification/forms/AdditionalInformation/hooks/useGetDefaultPepInfo.ts';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { TableHeader } from '~/components/ListCardTable/TableHeader.tsx';
import { AccountTable } from '~/modules/identification/forms/AdditionalInformation/TaxResident/AccountTable.tsx';
import { theme } from '~/theme';
import { useSelector } from 'react-redux';
import FormGridItem from '~/components/FormComponents/FormGridItem';
import { commonStyles } from '~/theme/styles/commonStyles';
import { selectIsReidentification } from '~/store/identification-form/selectors';

export const CRSIBlock = ({ index }: TAdditionalInfoFormContentProps) => {
  const { lastName, firstName, middleName } = useGetDefaultPepInfo(index);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isReidentification = useSelector(selectIsReidentification);
  const { setValue, control } = useFormContext<{ additionalInformation: TPepEntity[] }>();
  const field = `additionalInformation.${index}` as const;

  const {
    fields: jurisdictionFields,
    append: appendJurisdiction,
    remove: removeJurisdiction,
  } = useFieldArray({
    control,
    name: `${field}.jurisdictions`,
  });

  if (!jurisdictionFields.length) {
    appendJurisdiction(JURISDICTION);
  }

  const { fields: accountFields, append: appendAccount } = useFieldArray({
    control,
    name: `${field}.accounts`,
  });

  if (!accountFields.length) {
    appendAccount(ACCOUNT);
  }

  useEffect(() => {
    setValue(`${field}.lastName`, lastName as string);
    setValue(`${field}.firstName`, firstName as string);
    setValue(`${field}.middleName`, middleName as string);
  }, [lastName, firstName, middleName, setValue]);

  return (
    <Grid sx={styles.wrapper}>
      <Typography sx={{ ...styles.titleInfo, textAlign: 'center' }} variant="h5">
        Форма Документа Самостійної Оцінки Фізичної Особи
      </Typography>
      <Grid container spacing={3}>
        <FormGridItem>
          <FormInput
            name={`${field}.lastName`}
            label="Прізвище"
            placeholder="Введіть повне прізвище"
            disabled
            fullWidth
          />
        </FormGridItem>
        <FormGridItem>
          <FormInput
            name={`${field}.firstName`}
            label="Ім'я"
            placeholder="Введіть повне ім'я"
            disabled
            fullWidth
          />
        </FormGridItem>
        <FormGridItem>
          <FormInput
            name={`${field}.middleName`}
            label="По батькові (за наявності)"
            placeholder="Введіть по батькові (за наявності)"
            disabled
            fullWidth
          />
        </FormGridItem>
        <FormGridItem>
          <FormAutocomplete
            name={`${field}.appeals`}
            options={typeAppeals}
            inputProps={{
              placeholder: 'Оберіть тип звернення',
            }}
            label="Звернення (наприклад, Mr, Dr, Ms, Herr тощо)"
            shouldSaveLabel
          />
        </FormGridItem>
      </Grid>
      <Typography sx={{ ...styles.titleInfo, textAlign: 'center' }} variant="h5">
        Держава / юрисдикція резидентства для цілей оподаткування та відповідний номер платника
        податків
      </Typography>
      {jurisdictionFields.map((jurisdiction, jurisdictionIndex) => (
        <JurisdictionResidence
          key={jurisdiction.id}
          parentIndex={index}
          index={jurisdictionIndex}
          add={() => appendJurisdiction(JURISDICTION)}
          remove={() => removeJurisdiction(jurisdictionIndex)}
          isPlusShown={jurisdictionIndex === jurisdictionFields.length - 1}
          isMinusShown={jurisdictionFields.length > 1}
        />
      ))}
      {isReidentification && (
        <Box sx={styles.table}>
          {!isMobile && <TableHeader columns={infoAccountTableColumns} showActions={false} />}
          <Box sx={{ ...commonStyles.columnFlexContainer, gap: { sm: 2, md: 0, xs: 2 } }}>
            {accountFields.map((account, accountIndex: number) => (
              <AccountTable key={account.id} parentIndex={index} index={accountIndex} />
            ))}
          </Box>
        </Box>
      )}
    </Grid>
  );
};
